import { refineExactlyOneForm } from "./exactly-one-form.js";
import { z } from "zod";
//#region src/unsatisfied-demand.ts
/**
* D19 (lead rulings R-25 / R-26 / R-42) — **demand the data plane could not satisfy at observation
* time — includes node types that have live nodes but still have unschedulable pods.**
*
* # ⛔⛔ READ THAT SENTENCE AGAIN, BECAUSE THE OBVIOUS READING IS WRONG
*
* This is **NOT** "node types that never obtained a node". A type with four healthy live nodes and one
* pod stuck `Unschedulable` — quota exhausted, zone pressure, a taint — **belongs in this array**. The
* field was originally named `unobtainedDemand` and was renamed precisely because that name asserted
* something false about its own contents, which is the defect class this whole program exists to fix.
*
* ⛔ THE PRODUCER MUST NOT FILTER THIS AGAINST ITS OWN CENSUS (R-26). It reports the observation. The
* node census rides the SAME report, so a consumer can join the two itself — and a producer-side filter
* would **silently drop the third state** (has nodes, still cannot schedule), which is the state an
* operator is least likely to guess and most likely to be paged for. `A-319`/`A-320` machine-check that
* the schema permits it, so a later "helpful" refine cannot delete it.
*
* # ⛔ PACKAGE HOME — THIS PACKAGE, AND IT IS NOT NEGOTIABLE
*
* The same row shape rides TWO surfaces: `StatusReport.unsatisfiedDemand` (the DP→CP wire, in
* `@zion/sdk-cp-dp-contracts`) and `dpComputeViewSchema.unsatisfiedDemand` (the CP→console view, in
* THIS package). `contracts → contracts-cp-dp` is forbidden by `dependency-direction.test.ts` (SDK-002)
* and does not even resolve (`@zion/sdk-contracts`' only dependency is `zod`), so declaring it there
* would not compile here. Declared HERE it needs zero cross-package imports and contracts-cp-dp imports
* it in the legal direction — exactly the arrangement `compute-resolution.ts` records for itself.
*
* # ⛔ ABSENCE OF THE COLLECTION MEANS "NOT MEASURED"
*
* The three states are encoded POSITIONALLY, which is why no discriminated union is needed:
*  - **field absent** ⇒ not measured (a producer predating the field, a k3d/local DP with no node pools,
*    a failed pod list, a DP that has never reported). ⛔ Never "nothing is unsatisfied".
*  - **`[]`** ⇒ *we looked, and nothing is unsatisfied.* A real answer, and NOT the same as absent.
*  - **entries** ⇒ these node types could not be satisfied.
* ⛔ Do NOT `.default([])` either surface: that collapses the first two states, which is the same
* confident-zero defect one field over.
*/
/**
* Row cap on both `unsatisfiedDemand` surfaces, mirroring `POD_LIFECYCLE_MAX` and
* `COMPUTE_RESOLUTION_MAX`.
*
* ⛔ AN AVAILABILITY CONTROL, NOT TIDINESS — the posture `podLifecycle` and `computeResolutions`
* already take. The wire array is DP-ASSERTED and CP persists it, `StatusReportSchema` is `.strict()`
* end to end, and release-engine's ingest body limit is the measured ~100 kB express default: a 413
* kills the WHOLE report every cycle, and zod cannot express "the encoded array is under N kB". An
* over-cap array is **REJECTED, not trimmed**, so the one-truncator rule survives — the producer's
* slice is the only thing that ever drops a row.
*
* ⚠️ The value is MIRRORED rather than chosen. In practice this array is bounded by the DP's enabled
* machine-type count (single digits to low tens), so 200 is far above any real report; a tighter number
* would be an invented bound and no number at all would leave the body unbounded.
*
* ⛔ ASSERTED AS THE LITERAL `200` IN THIS PACKAGE'S TESTS, NEVER IMPORTED BACK from
* `POD_LIFECYCLE_MAX` — that import is the forbidden direction and would red
* `dependency-direction.test.ts`. `COMPUTE_RESOLUTION_MAX` has exactly this problem and solves it the
* same way (`compute-resolution.test.ts:133-138`); the cross-package equality is asserted on the
* contracts-cp-dp side, where both symbols resolve.
*/
const UNSATISFIED_DEMAND_MAX = 200;
/**
* One machine type whose demand the data plane could not satisfy.
*
* ⛔ THE EXISTENCE OF A ROW IS THE ASSERTION. There is no "count: 0" row and no "all clear" row — that
* state is the type's ABSENCE from the array, and the array's absence is "not measured". Three facts,
* three distinct representations, no overloaded value.
*/
const unsatisfiedDemandSchema = z.object({
	/**
	* The machine type whose demand could not be satisfied — the same keyspace as
	* `ComputeResolution.resolvedNodeType` and the `zion.witness.ai/node-type` pod nodeSelector,
	* bounded the same way (`.max(63)`, a k8s label-value bound) so a CP-side join on it cannot be fed
	* an unbounded string.
	*/
	nodeType: z.string().min(1).max(63),
	/**
	* Pods `Pending` with `PodScheduled=False`, reason `Unschedulable`, whose node-type selector names
	* {@link nodeType}.
	*
	* ⛔ `.positive()`, NOT `.nonnegative()`, and that is the design. A zero would mean "there is
	* unsatisfied demand, and it is none of it" — the row should not exist. Making it unrepresentable is
	* strictly better than accepting it and documenting a producer obligation.
	*
	* ⚠️ The attribution source is the pod's own `spec.nodeSelector['zion.witness.ai/node-type']`, NOT
	* the pod label. MEASURED: that key is stamped as a LABEL at exactly one site
	* (`compute-unit-provisioner.ts:1452`) and as a nodeSelector at the other two, so a label-based
	* aggregate is `null` for every fixture-service and worker pod and would have emitted an EMPTY LIST
	* on the very outage this field exists for.
	*/
	unschedulablePodCount: z.number().int().positive(),
	/**
	* Age of the OLDEST such pod, in seconds. Optional since 2026-10-10-status-report-since-timestamps
	* D-3: a row carries this key or {@link oldestUnschedulableSince}, exactly one. An operator that
	* predates D-2, or whose last fresh envelope did not carry `statusReportTimestamps: true`, sends
	* this form.
	*
	* ⚠️ THE DURATION IS THE LOAD-BEARING HALF. A two-second `Unschedulable` is an ordinary scale-up; a
	* twenty-minute one is the failure this whole program is about. A bare count would page on the
	* first. ⛔ `0` is legal and means *just now* — a pod that went Unschedulable this instant — so a
	* consumer must not treat `0` as "unknown" or coalesce anything else into it.
	*/
	oldestUnschedulableSinceSeconds: z.number().int().nonnegative().optional(),
	/**
	* The ISO 8601 instant at which the oldest such pod became unschedulable: the earliest
	* `PodScheduled.lastTransitionTime` of the row's pods, as `new Date(ms).toISOString()` writes it
	* (2026-10-10-status-report-since-timestamps D-2). The house instant shape of `reportedAt`:
	* `z.string().min(1)`, here also `.max(64)`.
	*
	* The value stays the same while the oldest pod stays pending, so the `unsatisfiedDemand`
	* section etag stays the same between cycles; {@link oldestUnschedulableSinceSeconds} grows by
	* the cycle length and moves the etag on every report. The reader derives the age as its own
	* clock minus `Date.parse(oldestUnschedulableSince)` (D-5).
	*
	* An operator sends this form, and omits the age, only while the last fresh desired-state
	* envelope carried `statusReportTimestamps: true` (D-4). A row carries exactly one of the two
	* forms (D-3).
	*/
	oldestUnschedulableSince: z.string().min(1).max(64).optional()
}).strict().superRefine((row, ctx) => refineExactlyOneForm(row, ctx, "an unsatisfiedDemand row", "oldestUnschedulableSinceSeconds", "oldestUnschedulableSince"));
//#endregion
export { UNSATISFIED_DEMAND_MAX, unsatisfiedDemandSchema };

//# sourceMappingURL=unsatisfied-demand.js.map